import get from 'lodash/get';

import ProductCard from '../ProductCard';
import { BundleSectionProducts } from './BundleSectionProducts';
import { MultiVariantProductCard } from './MultiVariantProductCard';
import {
  getAddonCurrentPrice,
  getAddonPrice,
  getProductCurrentPrice,
  getProductPrice,
  getPriceFromSellingPlanAllocations,
  isGlobalProductList,
  isGlobalProduct,
  isProductAvailable,
} from '../../utils';

export interface ProductsHandlerProps {
  bundle: Bundle;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  addProduct: (productId: GlobalVariant | GlobalProduct) => void;
  removeProduct: (productId: GlobalVariant | GlobalProduct) => void;
  selectedProducts: Record<string, number>;
  setSelectedProducts: (newSelectedProducts: Record<string, number>) => void;
  initialSelectedProducts: Record<string, number>;
  canAddProducts: boolean;
  hasSections?: boolean;
  onCardClick: (product) => void;
  sellingPlan?: string | number;
  discount?: {
    type: string;
    value: number;
  };
  subscriptionLines: any;
  shopConfiguration?: ShopConfiguration;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
}

export const ProductsHandler = ({
  bundle,
  productsInCollection,
  addProduct,
  removeProduct,
  selectedProducts,
  setSelectedProducts,
  canAddProducts,
  onCardClick,
  hasSections = false,
  discount,
  sellingPlan,
  initialSelectedProducts,
  shopConfiguration,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
}: ProductsHandlerProps): JSX.Element => {
  const BaBProductsToCartFlag = bundle.Type === 'dynamicBaB';
  const isClassicBab = bundle?.Type === 'buildABox';

  if (hasSections) {
    return (
      <BundleSectionProducts
        bundleType={bundle.Type}
        productsInCollection={productsInCollection}
        addProduct={addProduct}
        removeProduct={removeProduct}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        canAddProducts={canAddProducts}
        onCardClick={onCardClick}
        discount={discount}
        sellingPlan={sellingPlan}
        shopConfiguration={shopConfiguration}
        initialSelectedProducts={initialSelectedProducts}
        hideBaBSoldOutOptions={hideBaBSoldOutOptions}
        ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
      />
    );
  }

  if (isGlobalProductList(productsInCollection)) {
    return (
      <MultiVariantProductCard
        discount={discount}
        sellingPlan={sellingPlan}
        products={productsInCollection}
        onCardClick={onCardClick}
        addProduct={addProduct}
        removeProduct={removeProduct}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
        canAddProducts={canAddProducts}
        baBProductsToCartFlag={BaBProductsToCartFlag}
        initialSelectedProducts={initialSelectedProducts}
        shopConfiguration={shopConfiguration}
        hideBaBSoldOutOptions={hideBaBSoldOutOptions}
        ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
        isClassicBab={isClassicBab}
        ProductCard={ProductCard}
      />
    );
  }

  return (
    <>
      {productsInCollection.map((product: GlobalVariant) => {
        let price;
        let currentPrice;

        if (hideBaBSoldOutOptions && !product.available) {
          return null;
        }

        const isAvailable = isProductAvailable(
          product,
          shopConfiguration,
          ignoreBaBOutOfStockAllowance,
        );

        if (isGlobalProduct(product)) {
          currentPrice = getProductCurrentPrice(product);
          price =
            getProductPrice(product, discount) ||
            getPriceFromSellingPlanAllocations(product, sellingPlan) ||
            currentPrice;

          return (
            <ProductCard
              price={price}
              currentPrice={currentPrice}
              currency={get(window, ['Shopify', 'currency', 'active'], 'USD')}
              key={product.id}
              id={product.id as number}
              handle={product.handle}
              image={product.image as string}
              quantity={selectedProducts[product.id] || 0}
              title={product.title}
              available={isAvailable}
              onIncrease={() => canAddProducts && addProduct(product)}
              onDecrease={() => removeProduct(product)}
              onCardClick={(productInfo) =>
                onCardClick({
                  ...productInfo,
                  images: product.images,
                  productId: product.productId,
                  hasOneVariant: product.hasOnlyDefaultVariant,
                })
              }
              description={product.description}
              reviews={product?.reviews}
            />
          );
        }

        if (BaBProductsToCartFlag) {
          currentPrice = getProductCurrentPrice(product);
          price =
            getProductPrice(product, discount) ||
            getPriceFromSellingPlanAllocations(product, sellingPlan) ||
            currentPrice;
        }

        return (
          <ProductCard
            price={price}
            currentPrice={currentPrice}
            productId={product.productId}
            currency={get(window, ['Shopify', 'currency', 'active'], 'USD')}
            key={product.id}
            id={product.id as number}
            handle={product.handle}
            image={product.image as string}
            quantity={selectedProducts[product.id] || 0}
            subtitle={product.metafields?.bundle?.subtitle}
            title={product.name}
            available={isAvailable}
            onIncrease={() => canAddProducts && addProduct(product)}
            onDecrease={() => removeProduct(product)}
            onCardClick={(productInfo) =>
              onCardClick({
                ...productInfo,
                images: product.images,
                productId: product.productId,
                hasOneVariant: product.hasOnlyDefaultVariant,
              })
            }
            description={product.description}
            reviews={product?.reviews}
          />
        );
      })}
    </>
  );
};

export interface AddonsHandlerProps {
  bundle: Bundle;
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
  addAddon: (productId: GlobalVariant) => void;
  removeAddon: (productId: GlobalVariant) => void;
  selectedAddons: Record<string, number>;
  setSelectedAddons: (newSelectedProducts: Record<string, number>) => void;
  onCardClick: (product) => void;
  shopConfiguration?: ShopConfiguration;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
}

const AddonsHandler = ({
  productsInAddonsCollection,
  bundle,
  addAddon,
  removeAddon,
  selectedAddons,
  setSelectedAddons,
  onCardClick,
  shopConfiguration,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
}: AddonsHandlerProps): JSX.Element => {
  const baBProductsToCartFlag = bundle.Type === 'dynamicBaB';
  const isClassicBab = bundle?.Type === 'buildABox';

  return (
    <>
      {isGlobalProductList(productsInAddonsCollection) ? (
        <MultiVariantProductCard
          products={productsInAddonsCollection}
          onCardClick={onCardClick}
          addProduct={addAddon}
          removeProduct={removeAddon}
          selectedProducts={selectedAddons}
          setSelectedProducts={setSelectedAddons}
          baBProductsToCartFlag={true}
          isAddon={true}
          hideBaBSoldOutOptions={hideBaBSoldOutOptions}
          ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
          isClassicBab={isClassicBab}
          ProductCard={ProductCard}
        />
      ) : (
        productsInAddonsCollection.map((product) => (
          <ProductCard
            price={getAddonPrice(product)}
            currentPrice={getAddonCurrentPrice(product)}
            currency={get(window, ['Shopify', 'currency', 'active'], 'USD')}
            key={product.id}
            productId={product.productId}
            id={product.id as number}
            image={product.image as string}
            quantity={selectedAddons[product.id] || 0}
            title={product.name}
            subtitle={product.metafields?.bundle?.subtitle}
            available={isProductAvailable(
              product,
              shopConfiguration,
              ignoreBaBOutOfStockAllowance,
            )}
            onIncrease={() => addAddon(product)}
            onDecrease={() => removeAddon(product)}
            onCardClick={(productInfo) =>
              onCardClick({
                ...productInfo,
                images: product.images,
                productId: product.productId,
                handle: product.handle,
                hasOneVariant: product.hasOnlyDefaultVariant,
              })
            }
            description={product.description}
            doNotApplyDiscount={baBProductsToCartFlag}
            reviews={product?.reviews}
          />
        ))
      )}
    </>
  );
};

export const MainContent = {
  step2: (props): JSX.Element => <ProductsHandler {...props} />,
  step3: (props): JSX.Element => <AddonsHandler {...props} />,
};
